import { Versions } from '../constants/versions';
import { IExtensions } from '../resources/istatement/extensions';
import { InteractionComponent, IInteractionComponent } from './interaction_component';
import { Utils } from '../utils';
import { LanguageMap } from '../resources/istatement/language_map';

export interface IActivityDefinition {
	name?: LanguageMap;
	type?: string;
	description?: any;
	moreInfo?: string;
	extensions?: IExtensions;
	interactionType?: string;
	correctResponsesPattern?: any[];
	choices?: IInteractionComponent[];
	scale?: IInteractionComponent[];
	source?: IInteractionComponent[];
	target?: IInteractionComponent[];
	steps?: IInteractionComponent[];
}

export interface ActivityDefinitionParams {
	name?: LanguageMap;
	type?: string;
	description?: any;
	moreInfo?: string;
	extensions?: IExtensions;
	interactionType?: string;
	correctResponsesPattern?: any[];
	choices?: (InteractionComponent | IInteractionComponent)[];
	scale?: (InteractionComponent | IInteractionComponent)[];
	source?: (InteractionComponent | IInteractionComponent)[];
	target?: (InteractionComponent | IInteractionComponent)[];
	steps?: (InteractionComponent | IInteractionComponent)[];
	[key: string]: any;
}

export class ActivityDefinition {
	name?: LanguageMap;
	type?: string;
	description?: any;
	moreInfo?: string;
	extensions?: IExtensions;
	interactionType?: string;
	correctResponsesPattern?: any[];
	choices?: InteractionComponent[];
	scale?: InteractionComponent[];
	source?: InteractionComponent[];
	target?: InteractionComponent[];
	steps?: InteractionComponent[];

	constructor(cfg: ActivityDefinitionParams = {}) {
		const { name, type, description, moreInfo, extensions, correctResponsesPattern, interactionType } = cfg;

		const makeComponent = (items: any[] | undefined): InteractionComponent[] => {
			if (items === undefined) {
				return [];
			}
			const rv = items.map((item: InteractionComponent | IInteractionComponent) => {
				if (item instanceof InteractionComponent) {
					return item;
				}
				return new InteractionComponent(item);
			});
			return rv;
		};

		this.interactionType = interactionType;

		switch (interactionType) {
			case 'choice':
			case 'sequencing':
				if (cfg.choices) {
					this.choices = this.choices || [];
					this.choices = [...this.choices, ...makeComponent(cfg.choices)];
				}
				break;
			case 'likert':
				if (cfg.scale) {
					this.scale = this.scale || [];
					this.scale = [...this.scale, ...makeComponent(cfg.scale)];
				}
				break;
			case 'matching':
				if (cfg.source) {
					this.source = this.source || [];
					this.source = [...this.source, ...makeComponent(cfg.source)];
				}
				if (cfg.target) {
					this.target = this.target || [];
					this.target = [...this.target, ...makeComponent(cfg.target)];
				}
				break;
			case 'performance':
				if (cfg.steps) {
					this.steps = this.steps || [];
					this.steps = [...this.steps, ...makeComponent(cfg.steps)];
				}
				break;
		}

		this.name = name;
		this.type = type;
		this.description = description;
		this.moreInfo = moreInfo;
		this.extensions = extensions;
		this.correctResponsesPattern = correctResponsesPattern;
	}

	toString(lang: string = 'en-US'): string {
		if (this.name !== null) {
			return this.getLangDictionaryValue('name', lang);
		}

		if (this.description !== null) {
			return this.getLangDictionaryValue('description', lang);
		}

		return '';
	}

	asVersion(version: Versions = '1.0.3'): IActivityDefinition {
		const { name, type, description, interactionType, correctResponsesPattern, extensions } = this;
		const { choices, scale, source, target, steps } = this;

		const result: IActivityDefinition = {
			name,
			type,
			description,
			interactionType,
			correctResponsesPattern,
			extensions,
			choices: choices && choices.map((v) => v.asVersion(version)),
			scale: scale && scale.map((v) => v.asVersion(version)),
			source: source && source.map((v) => v.asVersion(version)),
			target: target && target.map((v) => v.asVersion(version)),
			steps: steps && steps.map((v) => v.asVersion(version))
		};

		return result;
	}

	getLangDictionaryValue = Utils.getLangDictionaryValue;

	static fromJSON(definitionJSON: string): ActivityDefinition {
		const _definition = JSON.parse(definitionJSON);

		return new ActivityDefinition(_definition);
	}
}
